import React, { useState, useEffect } from 'react';
import { clientAPI, serviceAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { Plus, Edit2, Eye, Building2, Download, Search, X, Phone, Mail, MapPin, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/dateFormat';

const CLIENT_CATEGORIES = ['Individual', 'Firm', 'LLP', 'Company', 'Trust', 'Other'];

const Clients = () => {
  const { isPartner, isManager } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('active'); // active, inactive, all
  const [filterCategory, setFilterCategory] = useState(''); // category filter
  
  // Client Detail Modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedClientDetail, setSelectedClientDetail] = useState(null);
  
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    category: '',
    gst_number: '',
    pan: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    services: [],
    file_number: '',
    digital_signatures: [],  // Array of DSCs
  });

  useEffect(() => {
    fetchClients();
    fetchServices();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await clientAPI.getAll();
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await serviceAPI.getAll();
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      category: '',
      gst_number: '',
      pan: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      services: [],
      file_number: '',
      digital_signatures: [],
    });
    setEditMode(false);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.category) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      // Clean empty strings to null for optional fields
      const cleanData = {
        name: formData.name,
        category: formData.category,
        gst_number: formData.gst_number || null,
        pan: formData.pan || null,
        contact_person: formData.contact_person || null,
        phone: formData.phone || null,
        email: formData.email || null,
        address: formData.address || null,
        services: formData.services || [],
        file_number: formData.file_number || null,
        digital_signatures: formData.digital_signatures || [],
      };
      
      if (editMode) {
        await clientAPI.update(formData.id, cleanData);
      } else {
        await clientAPI.create(cleanData);
      }
      resetForm();
      fetchClients();
    } catch (error) {
      console.error('Client error:', error.response?.data || error);
      alert(`Failed to ${editMode ? 'update' : 'create'} client: ${error.response?.data?.detail || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (client) => {
    setFormData({
      ...client,
      file_number: client.file_number || '',
      digital_signatures: client.digital_signatures || [],
    });
    setEditMode(true);
    setShowForm(true);
  };

  const handleServiceToggle = (serviceId) => {
    const newServices = formData.services.includes(serviceId)
      ? formData.services.filter(id => id !== serviceId)
      : [...formData.services, serviceId];
    setFormData({ ...formData, services: newServices });
  };

  const exportToExcel = () => {
    const headers = [
      'Client Code',
      'Name',
      'Category',
      'GST Number',
      'PAN',
      'Contact Person',
      'Phone',
      'Email',
    ];
    
    const rows = clients.map(client => [
      client.client_code || '',
      client.name,
      client.category,
      client.gst_number || '',
      client.pan || '',
      client.contact_person || '',
      client.phone || '',
      client.email || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clients-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Filter clients based on search and status
  const filteredClients = clients.filter((client) => {
    // Status filter
    if (filterStatus === 'active' && !client.is_active) return false;
    if (filterStatus === 'inactive' && client.is_active) return false;
    
    // Category filter
    if (filterCategory && client.category !== filterCategory) return false;
    
    // Search filter - includes phone and DSC holder names
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    
    // Check DSC holder names
    const dscHolderMatch = client.digital_signatures?.some(dsc => 
      dsc.holder_name?.toLowerCase().includes(search)
    );
    
    return (
      client.name?.toLowerCase().includes(search) ||
      client.client_code?.toLowerCase().includes(search) ||
      client.category?.toLowerCase().includes(search) ||
      client.gst_number?.toLowerCase().includes(search) ||
      client.pan?.toLowerCase().includes(search) ||
      client.phone?.includes(search) ||
      client.email?.toLowerCase().includes(search) ||
      client.file_number?.toLowerCase().includes(search) ||
      dscHolderMatch
    );
  });

  const canEdit = isPartner || isManager;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Clients</h2>
        <div className="flex space-x-2">
          <button
            onClick={exportToExcel}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
            data-testid="export-clients-button"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
          {canEdit && (
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
              data-testid="create-client-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </button>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, code, GST, PAN, phone, DSC holder..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="client-search-input"
            />
          </div>
          <div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              {CLIENT_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="active">Active Clients</option>
              <option value="inactive">Inactive Clients</option>
              <option value="all">All Clients</option>
            </select>
          </div>
        </div>
        {(searchTerm || filterStatus !== 'active' || filterCategory) && (
          <p className="text-sm text-gray-600 mt-2">
            Showing {filteredClients.length} of {clients.length} clients
          </p>
        )}
      </div>

      {showForm && (
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold mb-4">
            {editMode ? 'Edit Client' : 'New Client'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Client Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                  data-testid="client-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                  data-testid="client-category-select"
                >
                  <option value="">Select Category</option>
                  {CLIENT_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">GST Number</label>
                <input
                  type="text"
                  value={formData.gst_number}
                  onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">PAN</label>
                <input
                  type="text"
                  value={formData.pan}
                  onChange={(e) => setFormData({ ...formData, pan: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Contact Person</label>
                <input
                  type="text"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Address</label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                rows="2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Services</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {services.map((service) => (
                  <label key={service.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.services.includes(service.id)}
                      onChange={() => handleServiceToggle(service.id)}
                      className="rounded"
                    />
                    <span className="text-sm">{service.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* File Number */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">File Number</label>
                <input
                  type="text"
                  value={formData.file_number}
                  onChange={(e) => setFormData({ ...formData, file_number: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., FILE-001"
                />
              </div>
            </div>

            {/* Digital Signature Section - Multiple DSCs */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium">Digital Signatures (DSC)</h4>
                <button
                  type="button"
                  onClick={() => setFormData({
                    ...formData,
                    digital_signatures: [
                      ...formData.digital_signatures,
                      { id: Date.now().toString(), holder_name: '', expiry_date: '', tag_color: 'green' }
                    ]
                  })}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add DSC
                </button>
              </div>
              
              {formData.digital_signatures.length === 0 ? (
                <p className="text-sm text-gray-500">No digital signatures added. Click "Add DSC" to add one.</p>
              ) : (
                <div className="space-y-3">
                  {formData.digital_signatures.map((dsc, index) => (
                    <div key={dsc.id || index} className="p-3 border rounded-lg bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">Holder Name *</label>
                          <input
                            type="text"
                            value={dsc.holder_name || ''}
                            onChange={(e) => {
                              const updated = [...formData.digital_signatures];
                              updated[index] = { ...dsc, holder_name: e.target.value };
                              setFormData({ ...formData, digital_signatures: updated });
                            }}
                            className="w-full px-2 py-1 border rounded text-sm"
                            placeholder="Name on DSC"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Expiry Date</label>
                          <input
                            type="date"
                            value={dsc.expiry_date || ''}
                            onChange={(e) => {
                              const updated = [...formData.digital_signatures];
                              updated[index] = { ...dsc, expiry_date: e.target.value };
                              setFormData({ ...formData, digital_signatures: updated });
                            }}
                            className="w-full px-2 py-1 border rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Tag Color</label>
                          <select
                            value={dsc.tag_color || 'green'}
                            onChange={(e) => {
                              const updated = [...formData.digital_signatures];
                              updated[index] = { ...dsc, tag_color: e.target.value };
                              setFormData({ ...formData, digital_signatures: updated });
                            }}
                            className="w-full px-2 py-1 border rounded text-sm"
                          >
                            <option value="green">Green</option>
                            <option value="yellow">Yellow</option>
                            <option value="red">Red</option>
                            <option value="white">White</option>
                            <option value="blue">Blue</option>
                          </select>
                        </div>
                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => {
                              const updated = formData.digital_signatures.filter((_, i) => i !== index);
                              setFormData({ ...formData, digital_signatures: updated });
                            }}
                            className="px-2 py-1 text-sm bg-red-100 text-red-600 rounded hover:bg-red-200"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex space-x-2">
              <button
                type="submit"
                disabled={loading}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                data-testid="client-save-button"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Client Detail Modal */}
      {showDetailModal && selectedClientDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-xl font-bold">{selectedClientDetail.name}</h3>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedClientDetail(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Client Code</p>
                  <p className="font-medium">{selectedClientDetail.code || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Category</p>
                  <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                    {selectedClientDetail.category}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <span className={`inline-block px-2 py-1 text-xs rounded ${selectedClientDetail.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {selectedClientDetail.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p className="font-medium">{selectedClientDetail.created_at ? formatDate(selectedClientDetail.created_at) : '-'}</p>
                </div>
              </div>

              <hr />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 flex items-center"><FileText className="w-4 h-4 mr-1" /> GST Number</p>
                  <p className="font-medium">{selectedClientDetail.gst_number || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 flex items-center"><FileText className="w-4 h-4 mr-1" /> PAN</p>
                  <p className="font-medium">{selectedClientDetail.pan || '-'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 flex items-center"><FileText className="w-4 h-4 mr-1" /> File Number</p>
                  <p className="font-medium">{selectedClientDetail.file_number || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Digital Signatures (DSC)</p>
                  {selectedClientDetail.digital_signatures?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedClientDetail.digital_signatures.map((dsc, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <span className={`w-3 h-3 rounded-full inline-block ${
                            dsc.tag_color === 'red' ? 'bg-red-500' :
                            dsc.tag_color === 'yellow' ? 'bg-yellow-500' :
                            dsc.tag_color === 'blue' ? 'bg-blue-500' :
                            dsc.tag_color === 'white' ? 'bg-gray-200 border border-gray-400' :
                            'bg-green-500'
                          }`}></span>
                          <span className="font-medium text-sm">{dsc.holder_name}</span>
                          {dsc.expiry_date && (
                            <span className="text-xs text-gray-500">
                              (Exp: {formatDate(dsc.expiry_date)})
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="font-medium text-gray-400">No DSC</p>
                  )}
                </div>
              </div>

              <hr />

              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Contact Person</p>
                  <p className="font-medium">{selectedClientDetail.contact_person || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 flex items-center"><Phone className="w-4 h-4 mr-1" /> Phone</p>
                  <p className="font-medium">{selectedClientDetail.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 flex items-center"><Mail className="w-4 h-4 mr-1" /> Email</p>
                  <p className="font-medium">{selectedClientDetail.email || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 flex items-center"><MapPin className="w-4 h-4 mr-1" /> Address</p>
                  <p className="font-medium">{selectedClientDetail.address || '-'}</p>
                </div>
              </div>

              {selectedClientDetail.services && selectedClientDetail.services.length > 0 && (
                <>
                  <hr />
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Services</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedClientDetail.services.map((serviceId) => {
                        const service = services.find(s => s.id === serviceId);
                        return (
                          <span key={serviceId} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                            {service?.name || serviceId}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              <div className="flex justify-end space-x-2 pt-4">
                <button
                  onClick={() => navigate(`/reports?tab=client&clientId=${selectedClientDetail.id}`)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  View Work Report
                </button>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    handleEdit(selectedClientDetail);
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Edit Client
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredClients.map((client) => (
          <div 
            key={client.id} 
            className={`bg-white p-4 rounded-lg shadow border hover:shadow-md transition-shadow cursor-pointer ${!client.is_active ? 'opacity-60 border-red-300' : ''}`}
            onClick={() => {
              setSelectedClientDetail(client);
              setShowDetailModal(true);
            }}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-start space-x-2">
                <Building2 className="w-5 h-5 text-blue-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg">{client.name}</h3>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                      {client.category}
                    </span>
                    {!client.is_active && (
                      <span className="inline-block px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex space-x-1" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => navigate(`/reports?tab=client&clientId=${client.id}`)}
                  className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                  title="View Report"
                >
                  <Eye className="w-4 h-4" />
                </button>
                {canEdit && (
                  <>
                    <button
                      onClick={() => handleEdit(client)}
                      className="p-1 text-gray-600 hover:bg-gray-50 rounded"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm(`Are you sure you want to ${client.is_active ? 'deactivate' : 'activate'} ${client.name}?`)) {
                          try {
                            // Only send the fields that need updating
                            await clientAPI.update(client.id, { 
                              name: client.name,
                              category: client.category,
                              is_active: !client.is_active 
                            });
                            fetchClients();
                          } catch (error) {
                            console.error('Error:', error.response?.data || error);
                            alert('Failed to update client status: ' + (error.response?.data?.detail || 'Unknown error'));
                          }
                        }
                      }}
                      className={`p-1 rounded ${client.is_active ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                      title={client.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {client.is_active ? '✕' : '✓'}
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="text-sm text-gray-600 space-y-1">
              {client.gst_number && <p>GST: {client.gst_number}</p>}
              {client.pan && <p>PAN: {client.pan}</p>}
              {client.contact_person && <p>Contact: {client.contact_person}</p>}
              {client.phone && <p>Phone: {client.phone}</p>}
              {client.services && client.services.length > 0 && (
                <div className="mt-2">
                  <p className="font-medium text-gray-700">Services:</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {client.services.map((serviceId) => {
                      const service = services.find(s => s.id === serviceId);
                      return service ? (
                        <span key={serviceId} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                          {service.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredClients.length === 0 && !showForm && (
        <div className="text-center py-12">
          <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">
            {searchTerm ? 'No clients found matching your search.' : 'No clients yet. Add your first client!'}
          </p>
        </div>
      )}
    </div>
  );
};

export default Clients;
